import { withFormik } from 'formik';
import React, { useState } from 'react';
import { Col, Grid, Modal as BootstrapModal, Row } from 'react-bootstrap';
import ReactDOM from 'react-dom';
import { useTranslation, withTranslation } from 'react-i18next';
import { useHistory } from "react-router-dom";
import { assignVisitBranches, copyVisit, createVisit } from '../../../api/protocol';
import { HandleApiError } from '../../../components/Notifications/APIErrorHandler';
import { Save } from '../../../components/Icons/Icons';
import { Success } from '../../../components/Notifications/Notifications';
import { OffsetTypes, Types, VisitStages } from '../../../components/Protocols/Visit/Enums';
import { VisitHeaderForm } from '../../../components/Protocols/Visit/Header';
import { getProtocolVisitHeaderSchema } from "../../../components/Validations/FormValidationSchema";
import { useProtocolVisits } from '../Hooks';
import { VisitBranchSelector, useBranches } from '../Branches';


export const AddVisitModal = (props) => {
    const { isShowing, hide, protocolId, submit } = props;
    const { t } = useTranslation();

    return isShowing ? ReactDOM.createPortal(
        <React.Fragment>
            <BootstrapModal show={true} onHide={hide} bsSize="large" backdrop="static" >
                <BootstrapModal.Header closeButton>
                    <BootstrapModal.Title>{t('protocols.visit.addVisitTitle')}</BootstrapModal.Title>
                </BootstrapModal.Header>
                <BootstrapModal.Body>
                    <CreateVisitContainer protocolId={protocolId} onClose={hide} onSubmit={submit} />
                </BootstrapModal.Body>
            </BootstrapModal>
        </React.Fragment>, document.body
    ) : null;
};

const CreateVisitContainer = (props) => {
    let history = useHistory();
    const { protocolId } = props;
    const visits = useProtocolVisits(protocolId);
    const [protocolBranches] = useBranches(protocolId);

    const [step, setStep] = useState(1);
    const [createdVisit, setCreatedVisit] = useState(null);

    const handleVisitCreated = (visit) => {
        if (protocolBranches.length === 0) {
            history.push(`/admin/protocols/${protocolId}/visitsTemplate/${visit.crossVersionId}`);
            return;
        }
        setCreatedVisit(visit);
        setStep(2);
    };

    const handleBranchesFinish = async (branchIds) => {
        if (branchIds && branchIds.length > 0)
            await assignVisitBranches(protocolId, createdVisit.crossVersionId, branchIds);
        history.push(`/admin/protocols/${protocolId}/visitsTemplate/${createdVisit.crossVersionId}`);
    };

    const handleSkip = () => {
        history.push(`/admin/protocols/${protocolId}/visitsTemplate/${createdVisit.crossVersionId}`);
    };

    const handleCancel = () => {
        props.onClose();
    };

    if (step === 2 && createdVisit) {
        return (
            <VisitBranchStep
                protocolId={protocolId}
                onFinish={handleBranchesFinish}
                onSkip={handleSkip}
            />
        );
    }

    return (
        <CreateVisitFormik
            protocolId={protocolId}
            visits={visits}
            onSubmit={handleVisitCreated}
            onCancel={handleCancel}
        />
    );
}

const VisitBranchStep = ({ protocolId, onFinish, onSkip }) => {
    const { t } = useTranslation();
    const [branchIds, setBranchIds] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onFinish(branchIds);
        } catch (error) {
            HandleApiError(error);
            setIsSaving(false);
        }
    };

    return (
        <Grid fluid>
            <Row>
                <Col sm={12}>
                    <legend>{t("protocols.branches.visitBranches")}</legend>
                    <VisitBranchSelector
                        protocolId={protocolId}
                        selectedBranchIds={branchIds}
                        onChange={setBranchIds}
                    />
                </Col>
            </Row>
            <Row style={{ marginTop: '10px' }}>
                <Col sm={12}>
                    <button type="button" className="btn btn-success btn-right" onClick={handleSave} disabled={isSaving}>
                        <Save /> {t("buttons.save")}
                    </button>
                    <button type="button" className="btn btn-default btn-right" onClick={onSkip} style={{ marginRight: '10px' }} disabled={isSaving}>
                        {t("buttons.skip")}
                    </button>
                </Col>
            </Row>
        </Grid>
    );
};

const CreateVisitFormik = withTranslation()(withFormik({
    mapPropsToValues: (props) => ({ visit: props.visit }),
    //enableReinitialize

    validationSchema: getProtocolVisitHeaderSchema(),

    handleSubmit: (value, formikBag) => {
        let visit = value.visit;
        visit.type = visit.type.id;
        visit.stage = visit.stage.id;
        visit.offsetType = visit.offsetType?.id
        visit.offsetFrom = visit.offsetFrom?.crossVersionId

        createVisit(formikBag.props.protocolId, visit)
            .then(res => {
                formikBag.setSubmitting(false);
                Success("protocolNotifications.visit_Created");
                formikBag.props.onSubmit(res.data);
            }).catch(error => {
                console.error(error);
                formikBag.setSubmitting(false);
                HandleApiError(error);
            });
    },

    displayName: 'CreateVisit',

})(VisitHeaderForm));

export const CopyVisitModal = (props) => {
    const { isShowing, hide, protocolId, visitId, submit } = props;
    const { t } = useTranslation();

    return isShowing ? ReactDOM.createPortal(
        <React.Fragment>
            <BootstrapModal show={true} onHide={hide} bsSize="large" backdrop="static" >
                <BootstrapModal.Header closeButton>
                    <BootstrapModal.Title>{t('protocols.visit.copyVisitTitle')}</BootstrapModal.Title>
                </BootstrapModal.Header>
                <BootstrapModal.Body>
                    <CopyVisitContainer protocolId={protocolId} visitId={visitId} onClose={hide} onSubmit={submit} />
                </BootstrapModal.Body>
            </BootstrapModal>
        </React.Fragment>, document.body
    ) : null;
};


const CopyVisitContainer = (props) => {
    let history = useHistory();
    const { protocolId, visitId } = props;

    const visits = useProtocolVisits(protocolId);
    const [protocolBranches] = useBranches(protocolId);
    const [step, setStep] = useState(1);
    const [createdVisit, setCreatedVisit] = useState(null);

    const handleVisitCopied = (visit) => {
        if (protocolBranches.length === 0) {
            history.push(`/admin/protocols/${protocolId}/visitsTemplate/${visit.crossVersionId}`);
            return;
        }
        setCreatedVisit(visit);
        setStep(2);
    };

    const handleBranchesFinish = async (branchIds) => {
        if (branchIds && branchIds.length > 0)
            await assignVisitBranches(protocolId, createdVisit.crossVersionId, branchIds);
        history.push(`/admin/protocols/${protocolId}/visitsTemplate/${createdVisit.crossVersionId}`);
    };

    const handleSkip = () => {
        history.push(`/admin/protocols/${protocolId}/visitsTemplate/${createdVisit.crossVersionId}`);
    };

    const handleCancel = () => {
        props.onClose();
    };

    if (step === 2 && createdVisit) {
        return (
            <VisitBranchStep
                protocolId={protocolId}
                onFinish={handleBranchesFinish}
                onSkip={handleSkip}
            />
        );
    }

    if (!visitId || visits.length === 0)
        return null;

    let visit = visits.filter(x => x.crossVersionId == visitId)[0];
    if (!visit) return null;

    let visitCopy = JSON.parse(JSON.stringify(visit));
    visitCopy.title = visit.title + " - Copia";
    visitCopy.type = Types().find(x => x.id == visit.type);
    visitCopy.stage = VisitStages().find(x => x.id == visit.stage);
    visitCopy.offsetType = OffsetTypes().find(x => x.id == visit.offsetType);
    visitCopy.version = 0.1;

    return (
        <CopyVisitFormik
            protocolId={protocolId}
            visit={visitCopy}
            visits={visits}
            onSubmit={handleVisitCopied}
            onCancel={handleCancel}
        />
    );
}

const CopyVisitFormik = withTranslation()(withFormik({
    mapPropsToValues: (props) => ({ visit: props.visit }),

    validationSchema: getProtocolVisitHeaderSchema(),

    handleSubmit: (value, formikBag) => {
        let visit = value.visit;
        visit.type = visit.type.id;
        visit.stage = visit.stage.id;
        visit.offsetType = visit.offsetType?.id
        visit.offsetFrom = visit.offsetFrom?.crossVersionId

        copyVisit(formikBag.props.protocolId, visit.id, visit)
            .then(res => {
                formikBag.setSubmitting(false);
                Success("protocolNotifications.visit_Created");
                formikBag.props.onSubmit(res.data);
            }).catch(error => {
                console.error(error);
                formikBag.setSubmitting(false);
                HandleApiError(error);
            });
    },

    displayName: 'CopyVisit',

})(VisitHeaderForm));
